// ═══════════════════════════════════════════════════════════════════════════════
//  PANTALLA 7 — Revelación + Cierre de Venta
//  Documento Maestro v2.2 (docs/correccionesOnboarding.md), §7.1 y §7.2
//
//  Dos fases:
//    7.1  Procesamiento con los 4 mensajes dinámicos y variables reales del
//         payload — no son decorativos, el documento los especifica uno por uno.
//    7.2  Resultado: bloques a) espejo, b) causa raíz, c) plan, d) argumento
//         económico, e) CTA. En ese orden, con el copy literal del documento.
//
//  ⚠️  CASO NO CUBIERTO POR EL DOCUMENTO, resuelto explícitamente:
//      quien eligió "Cero absoluto" en Pantalla 4 salta la calibración, así que
//      no existe un nivel medido [X] que contrastar con el percibido [Y]. En ese
//      caso se OMITE el bloque a) "El espejo" en vez de rellenarlo con un dato
//      inventado. Inventarlo rompería el principio de honestidad que sostiene
//      todo el guion. Los bloques b), c), d) y e) se muestran íntegros porque
//      no dependen de la calibración.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  getProcessingMessages,
  getRevelationBlocks,
  REVELATION_BUTTON,
} from './onboardingContent';
import { buildLabels } from './onboardingLabels';
import { revela } from './sonidos';

// §7.1 Pantalla de procesamiento. [v2.2] Son 4 mensajes, no 5: el de deadline
// se sustituyo por el de estilo de feedback y se elimino el de cierre.
const MESSAGE_MS = 2600;

export default function Screen7_Revelacion({ payload, onConvert }) {
  const labels = buildLabels(payload);
  const messages = getProcessingMessages({
    contextLabel: labels.contextProcessing,
    feedbackLabel: labels.feedbackLabel,
    attemptsLabel: labels.attemptsLabel,
  });

  const [msgIndex, setMsgIndex] = useState(0);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex((i) => {
        if (i >= messages.length - 1) {
          clearInterval(id);
          setProcessing(false);
          // El plan aparece solo, tras doce segundos de procesamiento: sin un
          // sonido que lo anuncie, quien miró para otro lado se lo pierde.
          revela();
          return i;
        }
        return i + 1;
      });
    }, MESSAGE_MS);
    return () => clearInterval(id);
  }, [messages.length]);

  /* ── 7.1 Procesamiento ─────────────────────────────────────────────────── */

  if (processing) {
    return (
      <div className="ob-screen">
        <div className="ob-processing">
          <div className="ob-spinner" aria-hidden="true" />
          <p className="ob-processing-msg" key={msgIndex} aria-live="polite">
            {messages[msgIndex]}
          </p>
        </div>
      </div>
    );
  }

  /* ── 7.2 Resultado ─────────────────────────────────────────────────────── */

  const blocks = getRevelationBlocks({
    realLevel: labels.realLevel,
    perceivedLabel: labels.perceivedLabel,
    attemptsLabel: labels.attemptsLabel,
    contextLabel: labels.contextLabel,
    feedbackLabel: labels.feedbackLabel,
    aspirationLabel: labels.aspirationLabel,
  });

  const showMirror = Boolean(labels.realLevel);

  return (
    <div className="ob-screen">
      <p className="ob-eyebrow">Tu plan de entrenamiento está listo</p>

      {/* a) El espejo — solo si hubo calibración real que reflejar */}
      {showMirror && (
        <div className="ob-reveal-block ob-highlight">
          <p>{blocks.mirror}</p>
        </div>
      )}

      {/* b) La causa raíz */}
      <div className="ob-reveal-block">
        <p>{blocks.rootCause}</p>
      </div>

      {/* c) El plan */}
      <div className="ob-reveal-block ob-highlight">
        <p>{blocks.plan}</p>
      </div>

      {/* d) El argumento económico */}
      <div className="ob-reveal-block">
        <p>{blocks.economic}</p>
      </div>

      {/* e) CTA final */}
      <div className="ob-reveal-block">
        <p>{blocks.cta}</p>
      </div>

      <div className="ob-actions">
        <button type="button" className="ob-btn" onClick={onConvert}>
          {REVELATION_BUTTON}
        </button>
      </div>
    </div>
  );
}
